import { useEffect, useState } from "react";
import { Navigate, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, XCircle, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { api } from "@/lib/api";

export default function PaymentResult({ variant }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (variant !== "success" || !sessionId) return;
    let ticks = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
      } catch {}
      ticks += 1;
      if (ticks < 12) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [variant, sessionId]);

  if (variant === "cancel") {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center noise-overlay crosshair-grid px-5" data-testid="payment-cancel-page">
        <div className="max-w-md w-full border border-crimson/40 bg-card2/60 p-10 text-center">
          <XCircle className="w-12 h-12 text-crimson mx-auto" />
          <h1 className="font-display text-2xl uppercase tracking-wider text-parchment mt-5">Payment Cancelled</h1>
          <p className="text-stone-400 text-sm mt-4 leading-relaxed">No charge was made. Your kit is waiting whenever you're ready.</p>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/store" data-testid="cancel-store-link" className="gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.2em] font-semibold px-6 py-3 hover:bg-gold-light transition-colors">Back to the Armory</Link>
            <Link to="/" data-testid="cancel-home-link" className="border border-gold/40 font-condensed uppercase tracking-[0.2em] px-6 py-3 hover:border-gold hover:text-gold transition-colors">Home</Link>
          </div>
        </div>
      </main>
    );
  }

  if (!sessionId) return <Navigate to="/store" replace />;

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center noise-overlay crosshair-grid px-5" data-testid="payment-success-page">
      <div className="max-w-md w-full border border-gold/30 bg-card2/60 p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto animate-spin" />
            <h1 className="font-display text-2xl uppercase tracking-wider text-parchment mt-5">Confirming Payment</h1>
            <p className="font-mono text-xs text-stone-500 tracking-[0.25em] uppercase mt-3" data-testid="payment-confirming">Verifying with Stripe...</p>
          </>
        )}
        {state === "paid" && (
          <>
            <ShieldCheck className="w-12 h-12 text-gold mx-auto" />
            <h1 className="font-display text-2xl uppercase tracking-wider text-parchment mt-5">Deo Gratias</h1>
            <p className="text-stone-300 text-sm mt-4 leading-relaxed" data-testid="payment-paid-message">
              Your payment is confirmed and a receipt is on its way to your inbox.
            </p>
            <p className="font-mono text-[10px] text-stone-500 tracking-[0.25em] uppercase mt-2">Session {sessionId.slice(-10).toUpperCase()}</p>
            <div className="mt-8 flex justify-center gap-4">
              <Link to="/members" data-testid="success-members-link" className="gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.2em] font-semibold px-6 py-3 hover:bg-gold-light transition-colors">Members HQ</Link>
              <Link to="/store" data-testid="success-store-link" className="border border-gold/40 font-condensed uppercase tracking-[0.2em] px-6 py-3 hover:border-gold hover:text-gold transition-colors">Store</Link>
            </div>
          </>
        )}
        {state === "timeout" && (
          <>
            <LogoMark className="w-12 h-12 text-gold mx-auto" />
            <h1 className="font-display text-2xl uppercase tracking-wider text-parchment mt-5">Still Processing</h1>
            <p className="text-stone-400 text-sm mt-4 leading-relaxed">Stripe is taking longer than usual to confirm. Check Members HQ shortly — your record updates automatically.</p>
            <Link to="/members" data-testid="timeout-members-link" className="inline-block mt-8 gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.2em] font-semibold px-6 py-3 hover:bg-gold-light transition-colors">Members HQ</Link>
          </>
        )}
      </div>
    </main>
  );
}
